import { Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { PageTransition } from "./PageTransition";

// Critical path - loaded immediately
import Index from "@/pages/Index";
import NotFound from "@/pages/NotFound";

// Lazy-loaded pages - only loaded when needed
const Contact = lazy(() => import("@/pages/Contact"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Accessibility = lazy(() => import("@/pages/Accessibility"));
const Auth = lazy(() => import("@/pages/Auth"));
const SetupAdmin = lazy(() => import("@/pages/SetupAdmin"));

// Admin components - lazy loaded
const ProtectedRoute = lazy(() => import("@/components/admin/ProtectedRoute"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminDrinks = lazy(() => import("@/pages/admin/Drinks"));
const AdminFood = lazy(() => import("@/pages/admin/Food"));
const AdminEvents = lazy(() => import("@/pages/admin/Events"));
const AdminGallery = lazy(() => import("@/pages/admin/Gallery"));
const AdminReservations = lazy(() => import("@/pages/admin/Reservations"));
const AdminContacts = lazy(() => import("@/pages/admin/Contacts"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AuditLogs"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminApiSettings = lazy(() => import("@/pages/admin/ApiSettings"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/EmailTemplates"));

// Marketing pages - lazy loaded
const MarketingDashboard = lazy(() => import("@/pages/admin/marketing/Dashboard"));
const MarketingContacts = lazy(() => import("@/pages/admin/marketing/Contacts"));
const MarketingCampaigns = lazy(() => import("@/pages/admin/marketing/Campaigns"));
const MarketingCampaignEditor = lazy(() => import("@/pages/admin/marketing/CampaignEditor"));
const MarketingTemplates = lazy(() => import("@/pages/admin/marketing/Templates"));
const MarketingAnalytics = lazy(() => import("@/pages/admin/marketing/Analytics"));

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

export function AnimatedRoutes() {
  const location = useLocation();

  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/about" element={<PageTransition><AboutUs /></PageTransition>} />
          <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
          <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
          <Route path="/cookie-policy" element={<PageTransition><CookiePolicy /></PageTransition>} />
          <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
          <Route path="/accessibility" element={<PageTransition><Accessibility /></PageTransition>} />
          <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
          <Route path="/setup-admin" element={<PageTransition><SetupAdmin /></PageTransition>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminDashboard /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/drinks" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminDrinks /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/food" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminFood /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/events" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminEvents /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/gallery" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminGallery /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/reservations" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminReservations /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/contacts" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminContacts /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminUsers /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/audit-logs" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminAuditLogs /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminSettings /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/api-settings" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminApiSettings /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/email-templates" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><AdminEmailTemplates /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><MarketingDashboard /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing/contacts" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><MarketingContacts /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing/campaigns" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><MarketingCampaigns /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing/campaigns/new" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><MarketingCampaignEditor /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing/campaigns/:id" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><MarketingCampaignEditor /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing/templates" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><MarketingTemplates /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing/analytics" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout><MarketingAnalytics /></AdminLayout>
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}